import { useBackgroundQuery } from "@apollo/client";
import { QueryRef } from "@apollo/client/react/types/types";
import { WasteSubmissionsStaffSummaryQuery, SubmissionStatus } from "generated-graphql/graphql";
import {
  ReactNode,
  createContext,
  useContext,
  useDeferredValue,
  useMemo,
} from "react";
import { WASTE_SUBMISSIONS_STAFF_SUMMARY_QUERY } from "./api";
import { useFullOmnisearch } from "stores/omnisearchStore";
import {
  parseOmnisearch,
  stringifyOmnisearch,
} from "components/Omnisearch/FilterPopover";

// Default statuses (exclude FILING_COMPLETE and NOT_REPORTING)
// This matches the table's default submissionStatus filter
const DEFAULT_STATUSES = [
  SubmissionStatus.NotStarted,
  SubmissionStatus.InProgress,
  SubmissionStatus.EncampInitialReview,
  SubmissionStatus.InReview,
  SubmissionStatus.ProcessingSubmission,
];

/**
 * Build the effective search string by merging defaults with URL omnisearch.
 * URL values take precedence over defaults.
 * Exported for testing.
 */
export function mergeSearchWithDefaults(
  omnisearch: string,
  defaultSearch: string
): string {
  if (!omnisearch && defaultSearch) {
    return defaultSearch;
  }
  if (omnisearch && defaultSearch) {
    const userFilters = parseOmnisearch(omnisearch);
    const defaultFilters = parseOmnisearch(defaultSearch);
    const mergedFilters: Record<string, string> = {};
    for (const [key, value] of Object.entries(defaultFilters)) {
      if (!(key in userFilters)) {
        mergedFilters[key] = value;
      }
    }
    for (const [key, value] of Object.entries(userFilters)) {
      mergedFilters[key] = value;
    }
    return stringifyOmnisearch(mergedFilters);
  }
  return omnisearch || defaultSearch;
}

// Export constants for testing
export const __testing = {
  DEFAULT_STATUSES,
};

type WasteFulfillmentSummaryContextType = {
  summaryQueryRef: QueryRef<WasteSubmissionsStaffSummaryQuery>;
  // isPending is true while filters are transitioning (shows stale data with reduced opacity)
  isPending: boolean;
};

const WasteFulfillmentSummaryContext = createContext<
  WasteFulfillmentSummaryContextType | undefined
>(undefined);

export function useWasteFulfillmentSummaryContext() {
  const context = useContext(WasteFulfillmentSummaryContext);
  if (!context) {
    throw new Error(
      "useWasteFulfillmentSummaryContext must be used within a WasteFulfillmentSummaryProvider"
    );
  }
  return context;
}

type WasteFulfillmentSummaryProviderProps = {
  children: ReactNode;
  /**
   * Default search string with default filters (e.g., status defaults, date range).
   * URL-based filters take precedence over these defaults.
   */
  defaultSearch: string;
};

/**
 * Provider that reads omnisearch from the URL store (useSyncExternalStore)
 * and fires the summary chart query.
 *
 * All filter values (includeOverdue, onlyOverdue, materializationType, etc.)
 * flow through the search string. The server resolver extracts them from the
 * search via extractFilterOverrides, eliminating dual-path updates.
 */
export function WasteFulfillmentSummaryProvider({
  children,
  defaultSearch,
}: WasteFulfillmentSummaryProviderProps) {
  // Read omnisearch directly from URL store (synchronous, no effect chain)
  const omnisearch = useFullOmnisearch();

  // Build effective search by merging defaults with URL filters
  const effectiveSearch = useMemo(
    () => mergeSearchWithDefaults(omnisearch, defaultSearch),
    [omnisearch, defaultSearch]
  );

  // Use deferred value to prevent re-suspension when filter changes
  // This keeps showing the old data while new data loads in the background
  const deferredSearch = useDeferredValue(effectiveSearch);

  // isPending is true when filters have changed but deferred value hasn't caught up yet
  // This allows us to show stale data with reduced opacity during transitions
  const isPending = effectiveSearch !== deferredSearch;

  const [summaryQueryRef] = useBackgroundQuery(
    WASTE_SUBMISSIONS_STAFF_SUMMARY_QUERY,
    {
      variables: { search: deferredSearch },
      // IMPORTANT: Use cache-and-network to ensure we always refetch when variables change
      // Without this, Apollo may return stale cached data when filters change
      fetchPolicy: "cache-and-network",
    }
  );

  const value = useMemo(
    () => ({
      summaryQueryRef,
      isPending,
    }),
    [summaryQueryRef, isPending]
  );

  return (
    <WasteFulfillmentSummaryContext.Provider value={value}>
      {children}
    </WasteFulfillmentSummaryContext.Provider>
  );
}
